import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { AppShell } from "../components/layout/AppShell";
import { NeuButton } from "../components/neu/Neu";
import { useAuth } from "../context/AuthContext";
import { isAdmin, ADMIN_ROLES } from "../utils/role";
import { useCachedResource } from "../hooks/useCachedResource";
import { listDepartments } from "../api/departments";
import { listBranches, myCompany } from "../api/directory";
import { DepartmentList } from "../features/departments/DepartmentList";
import { DepartmentForm } from "../features/departments/DepartmentForm";
import { DeptScopeBar } from "../features/departments/DeptScopeBar";

const EDITORS = ADMIN_ROLES;

// صفحة الأقسام: شريط الشركة/الفروع في الأعلى + قائمة أقسام مصفّاة حسب الفرع.
export default function DepartmentsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [scope, setScope] = useState(null);
  const [editing, setEditing] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const canEdit = EDITORS.includes(user?.role);
  const canDelete = isAdmin(user);

  const { data, reload: load } = useCachedResource("departments", async () => {
    const [depts, brs, co] = await Promise.all([
      listDepartments(),
      listBranches().catch(() => []),
      myCompany().catch(() => null),
    ]);
    return { items: depts, branches: brs, company: co };
  });
  const items = data?.items || [];
  const branches = data?.branches || [];
  const company = data?.company || null;
  const loaded = data !== undefined;

  const filtered = useMemo(() => {
    if (!scope) return items;
    if (scope === "__none__") return items.filter((d) => !d.branch_id);
    return items.filter((d) => d.branch_id === scope);
  }, [items, scope]);

  const counts = useMemo(() => {
    const c = { total: items.length, unassigned: 0 };
    items.forEach((d) => {
      if (!d.branch_id) c.unassigned += 1;
      else c[d.branch_id] = (c[d.branch_id] || 0) + 1;
    });
    return c;
  }, [items]);

  const openAdd = (s) => { setEditing(null); setPrefill(s || null); setModalOpen(true); };
  const openEdit = (d) => { setEditing(d); setPrefill(null); setModalOpen(true); };
  const close = () => { setModalOpen(false); setEditing(null); setPrefill(null); };
  const onSaved = async () => { close(); await load(); };

  return (
    <AppShell title={t("departments.title")} subtitle={t("departments.subtitle")}
      actions={canEdit && loaded ? (
        <NeuButton variant="primary" data-testid="dept-add-btn"
                   className="flex items-center gap-2" onClick={() => openAdd(null)}>
          <Plus className="h-4 w-4" /> {t("departments.add")}
        </NeuButton>
      ) : null}>
      {loaded && items.length > 0 ? (
        <DeptScopeBar company={company} branches={branches}
                       scope={scope} onScope={setScope} counts={counts} />
      ) : null}
      {loaded ? (
        <DepartmentList departments={filtered} branches={branches} showBranch={!scope}
                        canEdit={canEdit} canDelete={canDelete}
                        onEdit={openEdit} onDeleted={load}
                        onQuickAdd={canEdit ? openAdd : null} />
      ) : null}
      <DepartmentForm open={modalOpen} initial={editing} prefill={prefill}
                      departments={items} onCancel={close} onSaved={onSaved} />
    </AppShell>
  );
}
